'use client';

import AppLayoutClient from '@components/shared/AppLayout/AppLayoutClient';
import { BackToHomepageBtn } from '@components/shared/BackToHomepageBtn/BackToHomepageBtn';
import { useClientConfig } from '@providers/ClientConfigProvider';
import Container from '@ui/Container/Container';
import Text, { TextVariant } from '@ui/Text/Text';
import Image from 'next/image';
import { useEffect } from 'react';

/**
 * Safely get client config values with fallback for when ClientConfigProvider is not available.
 * This can happen in the global-error boundary where providers aren't mounted.
 * In that case, the image won't load and GTM won't track, but the error message will display.
 */
function useSafeClientConfig(): { cdnUrl: string; gtmId: string } {
  try {
    const config = useClientConfig();
    return { cdnUrl: config.CDN_URL, gtmId: config.GTMID };
  } catch {
    // ClientConfigProvider not available (e.g., in global-error boundary)
    // Return empty strings - image won't load but text content will still display
    return { cdnUrl: '', gtmId: '' };
  }
}

export default function ErrorPage({ error }: { error: Error & { digest?: string } }) {
  const { cdnUrl, gtmId } = useSafeClientConfig();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <AppLayoutClient lang="en" gtmId={gtmId || undefined}>
      <Container childClassName="gap-y-8 md:gap-y-16 lg:h-screen h-full content-center lg:py-0">
        <div className="col-span-full content-center text-center lg:col-span-5 lg:text-left">
          <Text
            variant={TextVariant.TitleLG}
            className="font-the-future text-[3.5rem] font-medium sm:text-[4rem] md:text-[6rem]"
            content="500"
          ></Text>
          <Text variant={TextVariant.TitleLG} className="pt-4" content="Something went wrong on our end"></Text>
          <Text
            variant={TextVariant.TextBodyLG}
            className="pt-4"
            content="Our servers hit a snag, but our team’s already on it. The good news? Your AI agents are still learning and working behind the scenes. Try refreshing in a moment."
          ></Text>

          <BackToHomepageBtn />
        </div>
        <div className="col-span-full lg:col-span-7 relative flex items-center justify-center">
          <Image
            height={600}
            width={800}
            className="select-none"
            src={`${cdnUrl}/public/images/website/error-page.webp`}
            alt="error occurred"
          />
        </div>
      </Container>
    </AppLayoutClient>
  );
}
